import { useCallback, useRef } from 'react';
import { valueFromPlainText } from './mention-markup';

// MentionsInput already maps ordinary typing to @[Name](id) markup.
// Browser spellcheck does not: it rewrites the textarea's visible plain text
// (@Name) and either skips React onChange or lets MentionsInput treat the
// replacement as "delete the mention". Stop those events here and copy the
// corrected plain text back onto the existing markup tokens.

function isSpellcheckReplacement(event) {
  if (event.isComposing) {
    return false;
  }

  if (
    event.inputType === 'insertReplacementText' ||
    event.inputType === 'insertFromSpellcheck'
  ) {
    return true;
  }

  // Some browsers report spellcheck as insertText of the whole word.
  return event.inputType === 'insertText' && Boolean(event.data?.length > 1);
}

function commitPlainText(input, textarea) {
  const next = valueFromPlainText(input.value, textarea.value);

  if (next !== input.value) {
    input.onChange(next);
  }
}

export function useMentionDomSync(input) {
  const inputRef = useRef(input);
  const cleanupRef = useRef(null);

  inputRef.current = input;

  const inputCallbackRef = useCallback(element => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    if (!element) {
      return;
    }

    const onInput = event => {
      if (!isSpellcheckReplacement(event)) {
        return;
      }

      event.stopPropagation();
      commitPlainText(inputRef.current, event.target);
    };

    element.addEventListener('input', onInput, true);
    cleanupRef.current = () => {
      element.removeEventListener('input', onInput, true);
    };
  }, []);

  const onBlur = useCallback(event => {
    if (event.target && typeof event.target.value === 'string') {
      commitPlainText(inputRef.current, event.target);
    }

    inputRef.current.onBlur(event);
  }, []);

  return { inputRef: inputCallbackRef, onBlur };
}
